import { useMemo, useState } from "react";
import {
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaCopy,
  FaEnvelope,
  FaExternalLinkAlt,
  FaIdCard,
  FaLink,
  FaMapMarkerAlt,
  FaPhone,
  FaShieldAlt,
  FaUserCircle,
} from "react-icons/fa";
import { MdPayments } from "react-icons/md";
import { toast } from "react-toastify";

import { useAppContext } from "../contexts/AppContextProvider";
import { copyToClipboard } from "../utils/inputValidations";

const emptyValue = "—";

const currencySymbols = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
};

const displayValue = (value) =>
  value === null || value === undefined || value === "" ? emptyValue : value;

const formatDate = (value) => {
  if (!value) return emptyValue;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const getInitials = (name) =>
  String(name || "Partner")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "P";

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="group flex min-w-0 items-start gap-3 rounded-xl border border-borderColor bg-backgroundPrimary/40 p-4 transition-colors hover:border-accent/50 hover:bg-backgroundPrimary/70">
    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent  transition-colors text-white">
      <Icon size={16} />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-textSecondary">
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-medium text-textPrimary">
        {displayValue(value)}
      </p>
    </div>
  </div>
);

const Section = ({ icon: Icon, title, description, children }) => (
  <section className="rounded-2xl border border-borderColor bg-backgroundPrimary p-5 shadow-sm sm:p-6">
    <div className="mb-5 flex items-start gap-3">
      {/* {Icon && (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
          <Icon size={17} />
        </div>
      )} */}
      <div>
        <h2 className="text-lg font-semibold text-textPrimary">{title}</h2>
        {description && (
          <p className="text-sm text-textSecondary">{description}</p>
        )}
      </div>
    </div>
    {children}
  </section>
);

const PartnerProfile = () => {
  const { partnerInfo: loginResponse } = useAppContext();
  const [isCopied, setIsCopied] = useState(false);

  const partner = useMemo(
    () =>
      loginResponse?.partnerInfo ||
      loginResponse?.partner ||
      loginResponse ||
      {},
    [loginResponse],
  );

  const currency = String(partner.payoutCurrency || "").toUpperCase();
  const payoutValue =
    partner.payoutValue === null ||
    partner.payoutValue === undefined ||
    partner.payoutValue === ""
      ? emptyValue
      : `${currencySymbols[currency] || ""}${Number(
          partner.payoutValue,
        ).toLocaleString("en-IN", {
          maximumFractionDigits: 4,
        })} ${currency}`.trim();

  const copyReferralLink = async () => {
    if (!partner.referralLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(partner.referralLink);
      } else {
        const input = document.createElement("textarea");
        input.value = partner.referralLink;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.focus();
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setIsCopied(true);
      toast.success("Referral link copied");
      window.setTimeout(() => setIsCopied(false), 1800);
    } catch {
      toast.error("Could not copy referral link");
    }
  };

  const isActive =
    partner.isActive === true ||
    partner.isActive === 1 ||
    partner.isActive === "1";

  return (
    <main className="p-5">
      <div className="mx-auto max-w-7xl space-y-7">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-accent">
              <FaUserCircle size={14} /> Account centre
            </div>
            <h2>Partner profile</h2>
            <p className="mt-2 max-w-2xl text-sm text-textSecondary sm:text-base">
              Everything you need to review your partner account, commercial
              terms, and referral identity.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start rounded-full border border-borderColor bg-backgroundSecondary px-3 py-2 text-xs text-textSecondary sm:self-auto">
            <FaShieldAlt className="text-emerald-400" /> Secure partner account
          </div>
        </div>

        <section className="relative overflow-hidden rounded-2xl border border-borderColor bg-backgroundPrimary p-6 sm:p-8">
          <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-accent/15 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-emerald-400/5 blur-2xl" />
          <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full text-white bg-accent text-2xl font-bold">
                {partner.partnerName ? (
                  getInitials(partner.partnerName)
                ) : (
                  <FaUserCircle />
                )}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-bold text-textPrimary">
                    {displayValue(partner.partnerName)}
                  </h2>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${isActive ? "bg-emerald-500/15 text-emerald-400" : "bg-backgroundMain text-textSecondary"}`}
                  >
                    {isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="mt-1 text-sm text-textSecondary">
                  {displayValue(
                    partner.partnerDesignation || partner.partnerType,
                  )}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-textSecondary">
                  <span className="rounded-md bg-backgroundSecondary px-2 py-1">
                    ID: {displayValue(partner.partnerId || partner.id)}
                  </span>
                  <span className="rounded-md bg-backgroundPrimary/60 px-2 py-1">
                    {displayValue(partner.corporateId)}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:min-w-[350px]">
              <div className="rounded-2xl border border-white/10 bg-backgroundPrimary/45 p-4 backdrop-blur-sm">
                <FaCalendarAlt className="mb-3 text-accent" size={15} />
                <p className="text-xs uppercase tracking-wide text-textSecondary">
                  Member since
                </p>
                <p className="mt-1 text-sm font-semibold text-textPrimary">
                  {formatDate(partner.createdAt)}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-backgroundPrimary/45 p-4 backdrop-blur-sm">
                <FaLink className="mb-3 text-accent" size={15} />
                <p className="text-xs uppercase tracking-wide text-textSecondary">
                  Referral code
                </p>
                <div className="flex gap-3 justify-between mt-1">
                  <p className="mt-1 truncate text-sm font-semibold text-textPrimary">
                    {displayValue(partner.referralCode)}
                  </p>
                  {partner.referralCode && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(partner.referralCode)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FaCopy size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-2">
          <Section
            icon={FaIdCard}
            title="Partner details"
            description="Your registered identity and contact information."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoItem
                icon={FaBuilding}
                label="Corporate ID"
                value={partner.corporateId}
              />
              <InfoItem
                icon={FaIdCard}
                label="Partner ID"
                value={partner.partnerId || partner.id}
              />
              <InfoItem
                icon={FaEnvelope}
                label="Email address"
                value={partner.partnerEmail}
              />
              <InfoItem
                icon={FaPhone}
                label="Phone number"
                value={partner.partnerPhone}
              />
              <InfoItem
                icon={FaUserCircle}
                label="Designation"
                value={partner.partnerDesignation}
              />
              <InfoItem
                icon={FaMapMarkerAlt}
                label="Location"
                value={partner.location}
              />
            </div>
          </Section>

          <Section
            icon={MdPayments}
            title="Commercial details"
            description="The payout configuration associated with your partner account."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoItem
                icon={FaUserCircle}
                label="Partner type"
                value={partner.partnerType}
              />
              <InfoItem
                icon={MdPayments}
                label="Payout type"
                value={partner.payoutType}
              />
              <InfoItem
                icon={MdPayments}
                label="Payout value"
                value={payoutValue}
              />
              <InfoItem
                icon={FaCheckCircle}
                label="Account created"
                value={formatDate(partner.createdAt)}
              />
            </div>
          </Section>
        </div>

        <Section
          icon={FaLink}
          title="Referral program"
          description="Share your referral link to onboard new users under your partner account."
        >
          <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
            <InfoItem
              icon={FaIdCard}
              label="Referral code"
              value={partner.referralCode}
            />
            <div className="rounded-xl border border-borderColor bg-backgroundPrimary/40 p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-wide text-textSecondary">
                  Referral link
                </p>
                {partner.referralLink && (
                  <a
                    className="text-xs font-medium text-accent hover:underline"
                    href={partner.referralLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open link{" "}
                    <FaExternalLinkAlt className="ml-1 inline" size={10} />
                  </a>
                )}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="min-w-0 flex-1 truncate rounded-lg border border-borderColor bg-backgroundSecondary px-3 py-2.5 text-sm text-textPrimary">
                  {displayValue(partner.referralLink)}
                </div>
                <button
                  type="button"
                  onClick={copyReferralLink}
                  disabled={!partner.referralLink}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isCopied ? (
                    <FaCheckCircle size={14} />
                  ) : (
                    <FaCopy size={14} />
                  )}
                  {isCopied ? "Copied" : "Copy link"}
                </button>
              </div>
            </div>
          </div>
        </Section>
      </div>
    </main>
  );
};

export default PartnerProfile;
